// Color helpers for art content. These are the art's own colors (what the creator paints),
// never UI tokens: the editor chrome keeps the design-system surfaces.
export const hexToRgb = hex => {
  const value = parseInt(hex.slice(1), 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
};
export const rgbToHex = ({ r, g, b }) => `#${[r, g, b].map(channel => Math.round(Math.min(255, Math.max(0, channel))).toString(16).padStart(2, '0')).join('')}`;
export const withAlpha = (hex, alpha) => { const { r, g, b } = hexToRgb(hex); return `rgba(${r}, ${g}, ${b}, ${alpha})`; };
export function mix(a, b, amount) {
  const x = hexToRgb(a);
  const y = hexToRgb(b);
  return rgbToHex({ r: x.r + (y.r - x.r) * amount, g: x.g + (y.g - x.g) * amount, b: x.b + (y.b - x.b) * amount });
}
export function luminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const linear = channel => { const c = channel / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}
export const contrastRatio = (a, b) => { const [x, y] = [luminance(a), luminance(b)].sort((m, n) => n - m); return (x + 0.05) / (y + 0.05); };
export const readableInk = background => (contrastRatio(background, '#ffffff') >= contrastRatio(background, '#111111') ? '#ffffff' : '#111111');

// The swatches every picker offers after the brand and document colors.
export const DEFAULT_SWATCHES = ['#ffffff', '#f5f1ea', '#d9d4cc', '#8a8580', '#3b3936', '#111111',
  '#ff5a4e', '#ff8a3d', '#ffc83d', '#ffe9a8', '#8fd694', '#1f8a5b', '#1f4d3a',
  '#5ec8e5', '#2f6fde', '#1b2d5b', '#9b7cf0', '#e86bb4', '#ffd1dc'];

// A gradient stop as a CSS/canvas color (a fading stop becomes rgba).
export const stopColor = stop => (stop.opacity === undefined || stop.opacity >= 1 ? stop.color : withAlpha(stop.color, stop.opacity));

// CSS for a fill: swatches, the panel previews and the inline editor's highlight.
// Sorted, so a stop dragged past another still previews right.
const cssStops = stops => [...stops].sort((a, b) => a.offset - b.offset).map(stop => `${stopColor(stop)} ${Math.round(stop.offset * 100)}%`).join(', ');
export function fillToCss(fill) {
  if (!fill) return 'transparent';
  if (fill.type === 'linear') return `linear-gradient(${fill.angle}deg, ${cssStops(fill.stops)})`;
  if (fill.type === 'radial') return `radial-gradient(circle farthest-corner, ${cssStops(fill.stops)})`;
  if (fill.type === 'solid' && fill.opacity !== undefined && fill.opacity < 1) return withAlpha(fill.color, fill.opacity);
  if (fill.type === 'image') return fill.source?.url ? `url("${fill.source.url}") center / cover no-repeat` : 'transparent';
  return fill.color;
}
// A fill's representative color (a gradient's first stop), for ink and contrast checks.
export const fillColor = fill => (!fill || fill.type === 'image' ? null : fill.stops ? fill.stops[0].color : fill.color);

// Start and end points of a CSS linear-gradient angle across a w × h box, so the canvas
// paints a gradient exactly where CSS would.
export function gradientPoints(angle, width, height) {
  const radians = (angle * Math.PI) / 180;
  const dx = Math.sin(radians);
  const dy = -Math.cos(radians);
  const half = (Math.abs(width * dx) + Math.abs(height * dy)) / 2;
  const cx = width / 2;
  const cy = height / 2;
  return { x0: cx - dx * half, y0: cy - dy * half, x1: cx + dx * half, y1: cy + dy * half };
}

// Every distinct color a document paints, most used first: the "in this design" swatches.
export function documentColors(doc, limit = 10) {
  const counts = new Map();
  const add = color => { if (color) counts.set(color, (counts.get(color) || 0) + 1); };
  const addFill = fill => { if (!fill || fill.type === 'image') return; if (fill.stops) fill.stops.forEach(stop => add(stop.color)); else add(fill.color); };
  (doc?.pages || []).forEach(page => {
    addFill(page.background);
    page.layers.forEach(layer => {
      if (layer.type === 'text') { if (layer.style.fill) addFill(layer.style.fill); else add(layer.style.color); add(layer.style.highlight?.color); }
      if (layer.type === 'shape') { addFill(layer.fill); add(layer.stroke?.color); }
      if (layer.type === 'icon') add(layer.color);
    });
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([color]) => color).slice(0, limit);
}
